import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { WidgetDataView } from '@components/features/dashboard-widgets/WidgetDataView';
import { WidgetLoading } from '@components/features/dashboard-widgets/WidgetLoading';
import { showsNoDataNotice } from '@utils/widgetData';
import type { WidgetData } from '@app-types';
import { DashboardSectionError } from './DashboardSectionError';
import styles from './LaunchPageWidget.module.css';

/** A Dashboard Widget a Launch Page shows: its key, its catalog title and where the Launch Page loads it from. */
export interface LaunchPageWidgetDefinition {
  widgetKey: string;
  title: string;
  loadData: () => Promise<WidgetData>;
}

interface LaunchPageWidgetProps {
  widget: LaunchPageWidgetDefinition;
}

/**
 * A Dashboard Widget on a Launch Page: the same KPIs, chart and table as on the Dashboard and in the Widget
 * Subscription PDF, without the Dashboard's controls. Loads once on page load; a failure stays inside the widget.
 */
const LaunchPageWidget: React.FC<LaunchPageWidgetProps> = ({ widget: { widgetKey, title, loadData } }) => {
  const testId = `widget-${widgetKey}`;
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['launch-page-widget-data', widgetKey],
    queryFn: loadData,
    refetchOnMount: 'always'
  });

  const renderBody = () => {
    if (isError) {
      return (
        <DashboardSectionError
          message={`Failed to load ${title}.`}
          onRetry={() => refetch()}
          dataTestId={`${testId}-error`}
        />
      );
    }
    if (isLoading || !data) {
      return <WidgetLoading dataTestId={`${testId}-loading`} />;
    }

    return <WidgetDataView data={data} testIdPrefix={testId} showNoDataNotice={showsNoDataNotice(widgetKey)} />;
  };

  return (
    <section className={styles.widget} data-test-id={testId} aria-label={title}>
      <h3 className={styles.title}>{title}</h3>
      {renderBody()}
    </section>
  );
};

export { LaunchPageWidget };
